/**
 * ExposureLevelBadge Component
 * Visual badge for displaying exposure levels
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import {
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import {
  ExposureLevel,
  ExposureLevelLabels,
  ExposureLevelColors,
} from '../../types/riskApplication';

// ============================================================================
// TYPES
// ============================================================================

export interface ExposureLevelBadgeProps {
  /** The exposure level to display */
  level: ExposureLevel;
  /** Whether to show the label text */
  showLabel?: boolean;
  /** Size of the badge */
  size?: 'small' | 'medium';
  /** Show days of exposure */
  exposureDays?: number;
  /** Custom tooltip */
  tooltip?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getExposureDescription(level: ExposureLevel): string {
  switch (level) {
    case ExposureLevel.LOW:
      return 'Exposição ocasional ou em baixa concentração';
    case ExposureLevel.MEDIUM:
      return 'Exposição regular dentro dos limites de tolerância';
    case ExposureLevel.HIGH:
      return 'Exposição frequente próximo aos limites de tolerância';
    case ExposureLevel.VERY_HIGH:
      return 'Exposição contínua acima dos limites de tolerância';
  }
}

function formatExposureDays(days: number): string {
  if (days === 0) return 'Iniciou hoje';
  if (days === 1) return '1 dia de exposição';
  if (days < 30) return `${days} dias de exposição`;

  const months = Math.floor(days / 30);
  if (months === 1) return '1 mês de exposição';
  if (months < 12) return `${months} meses de exposição`;

  const years = Math.floor(months / 12);
  if (years === 1) return '1 ano de exposição';
  return `${years} anos de exposição`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const ExposureLevelBadge: React.FC<ExposureLevelBadgeProps> = ({
  level,
  showLabel = true,
  size = 'small',
  exposureDays,
  tooltip,
}) => {
  const color = ExposureLevelColors[level];
  const label = ExposureLevelLabels[level];
  const isCritical = level === ExposureLevel.HIGH || level === ExposureLevel.VERY_HIGH;

  const chipContent = showLabel ? label : undefined;
  const chipIcon = isCritical ? (
    level === ExposureLevel.VERY_HIGH ? (
      <ErrorIcon fontSize="small" />
    ) : (
      <WarningIcon fontSize="small" />
    )
  ) : undefined;

  const tooltipContent =
    tooltip ||
    (exposureDays !== undefined
      ? `${getExposureDescription(level)} - ${formatExposureDays(exposureDays)}`
      : getExposureDescription(level));

  return (
    <Tooltip title={tooltipContent} arrow>
      <Chip
        label={chipContent}
        icon={chipIcon}
        color={color as ChipProps['color']}
        size={size}
        variant="filled"
        sx={{
          minWidth: showLabel ? 80 : 32,
          fontWeight: isCritical ? 'bold' : 'normal',
        }}
      />
    </Tooltip>
  );
};

export default ExposureLevelBadge;
